import { api } from '@/lib/api';

export type RemoteImageImporter = (urls: string[]) => Promise<Record<string, string>>;

type JsonRecord = Record<string, unknown>;

function remoteImageUrl(source: string) {
  if (source.startsWith('/api/public/media/')) return undefined;
  try {
    const url = new URL(source, window.location.origin);
    if (!['http:', 'https:'].includes(url.protocol) || url.origin === window.location.origin) return undefined;
    return url.href;
  } catch {
    return undefined;
  }
}

function collectRemoteImageUrls(value: unknown, urls: Map<string, string>) {
  if (Array.isArray(value)) {
    value.forEach(item => collectRemoteImageUrls(item, urls));
    return;
  }
  if (!value || typeof value !== 'object') return;

  const record = value as JsonRecord;
  if (typeof record.src === 'string') {
    const url = remoteImageUrl(record.src);
    if (url) urls.set(record.src, url);
  }
  Object.values(record).forEach(item => collectRemoteImageUrls(item, urls));
}

function replaceRemoteImageUrls(value: unknown, replacements: Map<string, string>): unknown {
  if (Array.isArray(value)) return value.map(item => replaceRemoteImageUrls(item, replacements));
  if (!value || typeof value !== 'object') return value;

  const record = value as JsonRecord;
  return Object.fromEntries(
    Object.entries(record).map(([key, item]) => [
      key,
      key === 'src' && typeof item === 'string'
        ? (replacements.get(item) ?? item)
        : replaceRemoteImageUrls(item, replacements),
    ])
  );
}

/**
 * Rewrites externally hosted Tiptap image sources to media-library URLs before
 * content is persisted. Existing local assets are left untouched.
 */
export async function localizeContentImages<T extends JsonRecord>(
  content: T,
  importImages: RemoteImageImporter = async urls =>
    (await api.post<{ images: Record<string, string> }>('/media/import', { urls })).images
): Promise<T> {
  const sourceUrls = new Map<string, string>();
  collectRemoteImageUrls(content, sourceUrls);
  if (!sourceUrls.size) return content;

  const imported = await importImages([...new Set(sourceUrls.values())]);
  const replacements = new Map(
    [...sourceUrls].map(([source, url]) => {
      const localSource = imported[url];
      if (!localSource) throw new Error('Een geplakte afbeelding kon niet lokaal worden opgeslagen.');
      return [source, localSource];
    })
  );
  return replaceRemoteImageUrls(content, replacements) as T;
}
